import { hasRequestError } from '@/utils/guards'
import { createFetch } from '@vueuse/core'

const url = import.meta.url.split('/src')[0]

export interface DetailedError {
  detail: string
  source: {
    pointer: string
  }
  status: number
  title?: string
  code?: string
}

export type ApiRequestError = {
  errors: DetailedError[] | Record<string, string[]>
}

export const useApi = createFetch({
  baseUrl: import.meta.env.VITE_API_URL || `${url}/api`,
  options: {
    async beforeFetch(ctx) {
      const authStore = useAuthStore()

      ctx.options.headers = {
        ...ctx.options.headers,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authStore.token?.id ?? ''}`,
      }

      return ctx
    },
    async onFetchError(ctx) {
      const authStore = useAuthStore()
      const toast = useToast()

      if (hasRequestError<never, ApiRequestError>(ctx.data)) {
        if (Array.isArray(ctx.data.errors)) {
          const [error] = ctx.data.errors

          if (error.code === 'TOKEN_DECODE_ERROR') {
            toast.show({
              type: 'error',
              title: error.title,
              detail: error.detail,
            })
          }

          if (error.code === 'TOKEN_EXPIRATION_ERROR') {
            const { data, error } = await useFetch(
              `${import.meta.env.VITE_API_URL}/auth/refresh_token`,
            )
              .post({
                refresh_token: authStore.token?.refresh,
              })
              .json<{ idToken: string }>()

            if (error.value) {
              toast.show({
                type: 'error',
                title: 'Something went wrong',
                detail:
                  'Please reload this page. If the problem persists, try again later or contact support.',
              })
            }

            if (data.value && !error.value) {
              authStore.setToken({
                id: data.value.idToken,
                refresh: authStore.token?.refresh!,
              })

              await useApi(ctx.response?.url!)
            }
          }
        } else {
          ctx.error = ctx.data.errors
        }
      }

      return ctx
    },
  },
  fetchOptions: {
    mode: 'cors',
    credentials: 'include',
  },
})
